import {
  RUNNING_SURFACES,
  POOL_LENGTHS,
  SESSION_KINDS,
  RUN_TYPES,
  RUN_TYPE_LABELS,
  emptySportPrescription,
  normalizeSessionKind,
  pacePartsFromSec,
  paceSecFromMinSec,
} from '../../utils/trainingSportSession';

/** Editor de plantilla Deportes en el día del plan (coach). */
export default function SportDayPrescriptionEditor({
  sessionKind,
  prescription,
  coachNotes = '',
  onKindChange,
  onPrescriptionChange,
  onCoachNotesChange,
  onCoachNotesBlur,
  techniqueUrl = '',
  onTechniqueUrlChange,
  onTechniqueUrlBlur,
  onAssignSport,
  assigning = false,
  assignMessage = '',
}) {
  const kind = normalizeSessionKind(sessionKind);
  const rx = prescription || emptySportPrescription(kind);

  const setKind = (next) => {
    const k = normalizeSessionKind(next);
    // Un solo update en el padre (evita pisar Natación con Running por closure stale).
    onKindChange?.(k);
  };

  const patch = (field, value) => {
    onPrescriptionChange?.({ ...rx, [field]: value });
  };

  return (
    <div className="mt-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50/60 space-y-3">
      <div>
        <p className="text-xs font-bold text-stone-700 uppercase tracking-wide m-0">Tipo de sesión</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {[
            { id: SESSION_KINDS.GYM, label: 'Gimnasio' },
            { id: SESSION_KINDS.RUNNING, label: 'Deportes · Running' },
            { id: SESSION_KINDS.SWIMMING, label: 'Deportes · Natación' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={kind === opt.id ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => setKind(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {kind !== SESSION_KINDS.GYM ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <label className="text-xs font-bold text-amber-900 uppercase tracking-wide">
            Notas del coach
          </label>
          <textarea
            className="input w-full text-sm mt-1 min-h-[5.5rem] resize-y text-stone-900 bg-white"
            rows={4}
            maxLength={4000}
            value={coachNotes}
            placeholder="Indicaciones para el deportista (técnica, series, material…)"
            onChange={(e) => onCoachNotesChange?.(e.target.value)}
            onBlur={(e) => onCoachNotesBlur?.(e.target.value)}
          />
          <p className="text-[11px] text-stone-600 m-0 mt-1">
            El atleta verá esta nota en su diario Running / Natación.
          </p>
        </div>
      ) : null}

      {kind !== SESSION_KINDS.GYM ? (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-3">
          <label className="text-xs font-bold text-violet-900 uppercase tracking-wide">
            Video de técnica (opcional)
          </label>
          <input
            type="text"
            className="input w-full text-sm mt-1 h-8"
            value={techniqueUrl || ''}
            placeholder="https://youtube.com/watch?v=... (opcional)"
            onChange={(e) => onTechniqueUrlChange?.(e.target.value)}
            onBlur={(e) => onTechniqueUrlBlur?.(e.target.value)}
          />
          <p className="text-[11px] text-stone-600 m-0 mt-1">
            No es obligatorio. El atleta solo ve el botón si pegas un enlace.
          </p>
        </div>
      ) : null}

      {kind === SESSION_KINDS.RUNNING ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-bold text-stone-600">Superficie / pista</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {RUNNING_SURFACES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={rx.surface === s.id ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => patch('surface', s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold text-stone-600">Tipo de carrera</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {RUN_TYPES.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={rx.run_type === id ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => patch('run_type', id)}
                >
                  {RUN_TYPE_LABELS[id]}
                </button>
              ))}
            </div>
          </div>
          {rx.run_type === 'intervals' ? (
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-stone-600">Series (texto para el atleta)</label>
              <input
                type="text"
                className="input w-full text-sm h-8 mt-1"
                maxLength={500}
                value={rx.intervals_note || ''}
                placeholder="Ej. 6×400 m, recuperar 90 s"
                onChange={(e) => patch('intervals_note', e.target.value)}
              />
            </div>
          ) : null}
          <div>
            <label className="text-xs font-bold text-stone-600">Zona objetivo (1–5)</label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((z) => (
                <button
                  key={z}
                  type="button"
                  className={Number(rx.zone_target) === z ? 'btn-primary text-xs px-2' : 'btn-secondary text-xs px-2'}
                  onClick={() => patch('zone_target', z)}
                >
                  {z}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">FC objetivo (ppm)</label>
            <input
              type="number"
              className="input w-full text-sm h-8 mt-1"
              value={rx.hr_target_ppm ?? ''}
              placeholder="Opcional"
              onChange={(e) => patch('hr_target_ppm', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">Pace objetivo (min:seg / km)</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={0}
                className="input w-full text-sm h-8"
                value={pacePartsFromSec(rx.pace_target_sec_per_km).min}
                placeholder="5"
                onChange={(e) => {
                  const parts = pacePartsFromSec(rx.pace_target_sec_per_km);
                  const sec = paceSecFromMinSec(e.target.value, parts.sec || 0);
                  patch('pace_target_sec_per_km', sec > 0 ? sec : null);
                }}
              />
              <span className="text-stone-500">:</span>
              <input
                type="number"
                min={0}
                max={59}
                className="input w-full text-sm h-8"
                value={pacePartsFromSec(rx.pace_target_sec_per_km).sec}
                placeholder="30"
                onChange={(e) => {
                  const parts = pacePartsFromSec(rx.pace_target_sec_per_km);
                  const sec = paceSecFromMinSec(parts.min || 0, e.target.value);
                  patch('pace_target_sec_per_km', sec > 0 ? sec : null);
                }}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">Distancia meta (km)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              className="input w-full text-sm h-8 mt-1"
              value={rx.distance_target_km ?? ''}
              placeholder="Ej. 8.2"
              onChange={(e) => patch('distance_target_km', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          <div className="sm:col-span-2 rounded-lg border border-stone-200 bg-white p-2">
            <label className="flex items-center gap-2 text-xs font-bold text-stone-700">
              <input
                type="checkbox"
                className="rounded border-stone-300"
                checked={Boolean(rx.include_brick)}
                onChange={(e) => patch('include_brick', e.target.checked)}
              />
              Incluir transición bici→run (brick)
            </label>
            {rx.include_brick ? (
              <input
                type="number"
                className="input w-full text-sm h-8 mt-2"
                value={rx.transition_bike_run_pace_sec ?? ''}
                placeholder="Ritmo brick (segundos)"
                onChange={(e) => patch(
                  'transition_bike_run_pace_sec',
                  e.target.value === '' ? null : Number(e.target.value),
                )}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {kind === SESSION_KINDS.SWIMMING ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-bold text-stone-600">Tipo de piscina</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {POOL_LENGTHS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={Number(rx.pool_length_m) === m ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => patch('pool_length_m', m)}
                >
                  {m} m
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">Zona objetivo (1–5)</label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((z) => (
                <button
                  key={z}
                  type="button"
                  className={Number(rx.zone_target) === z ? 'btn-primary text-xs px-2' : 'btn-secondary text-xs px-2'}
                  onClick={() => patch('zone_target', z)}
                >
                  {z}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">FC objetivo (ppm)</label>
            <input
              type="number"
              className="input w-full text-sm h-8 mt-1"
              value={rx.hr_target_ppm ?? ''}
              placeholder="Opcional"
              onChange={(e) => patch('hr_target_ppm', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-stone-600">Ritmo medio /100 m (seg)</label>
            <input
              type="number"
              className="input w-full text-sm h-8 mt-1"
              value={rx.pace_per_100_sec ?? ''}
              placeholder="Opcional"
              onChange={(e) => patch('pace_per_100_sec', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>
      ) : null}

      {kind !== SESSION_KINDS.GYM ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
          <button
            type="button"
            className="btn-primary text-sm w-full sm:w-auto"
            disabled={assigning}
            onClick={() => onAssignSport?.()}
          >
            {assigning ? 'Guardando…' : 'Asignar entreno Deportes'}
          </button>
          <p className="text-xs text-stone-600 m-0">
            Confirma y guarda este día como Running/Natación en el plan del cliente. El deportista lo verá en su diario (tiempo digitado).
          </p>
          {assignMessage ? (
            <p className="text-xs font-semibold text-emerald-800 m-0 sm:basis-full">{assignMessage}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
